import { areNumbersEqual, createGrouping, uuid } from '../base';
import { Account, Amount, Counterparty, Currency, Entry, Ref, Transaction, TransactionBuilder } from '../domain';
import { ColumnFormat } from './Format';
import { RegisterEntry } from '../processing';
import { Row } from './DelimitedTextFile';

class AccountContainer {

  private _accountList: Account[];
  private _fileAccountList: string[];
  private _newAccounts: Account[] = [];
  private _accountIndex: Record<string, Account> = {};

  constructor(fileAccountList: string[], accountList: Account[]) {
    this._fileAccountList = fileAccountList;
    this._accountList = accountList;
  }

  parse() {
    const newAccountListener = (acc: Account) => {
      this._accountIndex[acc.fullName()] = acc;
      this._newAccounts.push(acc);
    };
    this._accountList.forEach((acc: Account) => { this._accountIndex[acc.fullName()] = acc; });
    this._fileAccountList
      .filter(acc => acc && acc !== "")
      .forEach(acc => Account.fromName(acc, this._accountIndex, newAccountListener));
  }

  getIndex() {
    return this._accountIndex;
  }

  getNew() {
    return this._newAccounts;
  }

  remove(accountList: string[]) {
    accountList.forEach(accName => {
      const index = this._newAccounts.findIndex(acc => acc.fname === accName);
      if (index >= 0) {
        this._newAccounts.splice(index, 1);
      }
    });
  }
}

class CounterpartyContainer {
  private _counterpartyList: Counterparty[];
  private counterpartyNames: string[];
  private _newCounterparties: Counterparty[] = [];
  private _counterpartyIndex: Record<string, Counterparty> = {};

  constructor(counterpartyNames: string[], accountList: Counterparty[]) {
    this.counterpartyNames = counterpartyNames;
    this._counterpartyList = accountList;
  }

  parse() {
    this._counterpartyList.forEach((cp: Counterparty) => { this._counterpartyIndex[cp.name] = cp; });
    this.counterpartyNames.forEach((cpname: string) => {
      if (!cpname) {
        return;
      }
      if (!(cpname in this._counterpartyIndex)) {
        const cp = new Counterparty(uuid(), cpname);
        this._counterpartyIndex[cpname] = cp;
        this._newCounterparties.push(cp);
      }
    });
  }

  getIndex() {
    return this._counterpartyIndex;
  }

  getNew() {
    return this._newCounterparties;
  }

  remove(cpList: string[]) {
    cpList.forEach(accName => {
      const index = this._newCounterparties.findIndex(acc => acc.name === accName);
      if (index >= 0) {
        this._newCounterparties.splice(index, 1);
      }
    });
  }
}

class EntityContainer {

  private _accounts: Account[];
  private _counterparties: Counterparty[];
  private _transactions: Transaction[];

  constructor(accounts: Account[], counterparties: Counterparty[], transactions: Transaction[]) {
    this._accounts = accounts;
    this._counterparties = counterparties;
    this._transactions = transactions;
  }

  get accounts() { return this._accounts; }
  get counterparties() { return this._counterparties; }
  get transactions() { return this._transactions; }

}

class ParsedRow {
  private _row: Row;
  private _errors: string[];
  private _registryEntry: RegisterEntry | undefined;

  constructor(row: Row, errors: string[], registryEntry?: RegisterEntry) {
    this._row = row;
    this._errors = errors;
    this._registryEntry = registryEntry;
  }

  hasError() { return this._errors.length !== 0; }

  get row() { return this._row; }
  get errors() { return this._row.hasError() ? [this._row.error] : this._errors; }
  get registryEntry() { return this._registryEntry; }

  withError(error:string) : ParsedRow {
    return new ParsedRow(this._row, [error], this._registryEntry);
  }
}


class ImportProcessor {
  private _format: ColumnFormat;
  private _data: Row[];
  private _accountContainer: AccountContainer = new AccountContainer([], []);
  private _accountList: Account[] = [];
  private _counterpartyContainer: CounterpartyContainer = new CounterpartyContainer([], []);
  private _counterpartyList: Counterparty[] = [];
  private _currency: Currency;
  private _invalidTxEntries: ParsedRow[] = [];
  private _invalidEntries: ParsedRow[] = [];
  private _transactions: Transaction[] = [];
  private _validEntries: ParsedRow[] = [];


  constructor(data: Row[], format: ColumnFormat, accountList: Account[], counterpartyList: Counterparty[], currency: Currency) {
    this._data = data;
    this._format = format;
    this._accountList = accountList;
    this._counterpartyList = counterpartyList;
    this._currency = currency;
  }

  process() {
    this.mapAccounts();
    this.mapCounterparties();
    this.readEntries();
    this.readTransactions();
  }

  mapAccounts() {
    const index = this._format.getColumnIndex("Account");
    const accountColumn = this._data.map(row => row.getData(index)).filter(a => a !== undefined);
    this._accountContainer = new AccountContainer(accountColumn, this._accountList);
    this._accountContainer.parse();
  }

  mapCounterparties() {
    const index = this._format.getColumnIndex("Counterparty");
    const counterpartyColumn = this._data.map(row => row.getData(index)).filter(a => a !== undefined);
    this._counterpartyContainer = new CounterpartyContainer(counterpartyColumn, this._counterpartyList);
    this._counterpartyContainer.parse();
  }

  readEntries() {
    const accountColIndex = this._format.getColumnIndex("Account");
    const counterpartyColIndex = this._format.getColumnIndex("Counterparty");
    const dateColIndex = this._format.getColumnIndex("Date");
    const amountColIndex = this._format.getColumnIndex("Amount");
    const currencyRef: Ref<Currency> = this._currency.getRef();
    const accountIndex = this._accountContainer.getIndex();
    const counterpartyIndex = this._counterpartyContainer.getIndex();

    this._data
      .filter(row => !row.isEmpty())
      .forEach(row => {
        const dateStr = row.getData(dateColIndex);
        const amountStr = row.getData(amountColIndex);
        const accountKey = row.getData(accountColIndex);
        const counterpartyKey = row.getData(counterpartyColIndex);
        const amountValue = parseFloat(amountStr);
        const date = new Date(dateStr);

        let errors = [];
        if (!accountKey) {
          errors.push("Account is invalid");
        }
        if (!counterpartyKey) {
          errors.push("Counterparty is invalid");
        }
        if (isNaN(amountValue)) {
          errors.push("Amount is invalid");
        }
        if (isNaN(date.getDate())) {
          errors.push("Date is invalid, 'YYYY/MM/DD' expected");
        }
        if (errors.length !== 0) {
          this._invalidEntries.push(new ParsedRow(row, errors));
          return;
        }

        const amount = new Amount(amountValue, currencyRef);
        const accountRef = accountIndex[accountKey].getRef();
        const counterpartyRef = counterpartyIndex[counterpartyKey].getRef();
        const registryEntry = new RegisterEntry(new Entry(uuid(), accountRef, amount), date, counterpartyRef);
        this._validEntries.push(new ParsedRow(row, [], registryEntry));
      });
  }


  readTransactions() {
    const validRows = this._validEntries.filter(parsedRow => !parsedRow.hasError());
    const grouped: Record<string, ParsedRow[]> = createGrouping(validRows, ent => this.registerEntryKey(ent));

    let transactions: Transaction[] = [];
    let invalidEntries: ParsedRow[] = [];

    const mapped = Object.values(grouped)
      .map(this.extractTransactions)
      .forEach(([txs, entries]) => {
        transactions = transactions.concat(txs);
        invalidEntries = invalidEntries.concat(entries);
      });

    this._transactions = transactions;
    this._invalidTxEntries = invalidEntries;
    this.removeInvalidEntriesAccountCounterparty();
  }

  private removeInvalidEntriesAccountCounterparty() {
    const invalidRows = this.getInvalidRows();
    const accountColIndex = this._format.getColumnIndex("Account");
    const counterpartyColIndex = this._format.getColumnIndex("Counterparty");
    
    const invalidEntryAccounts = invalidRows
      .map(parsedRow => parsedRow.row)
      .map(row => row.getData(accountColIndex));
    
    const invalidEntryCps = invalidRows
      .map(parsedRow => parsedRow.row)
      .map(row => row.getData(counterpartyColIndex));

    if (invalidEntryAccounts.length === 0 || invalidEntryCps.length === 0) {
      return;
    }
      
    const allAssignedAccounts = this._transactions.flatMap(tx => tx.entries.map(ent => ent.account.obj().fname));
    const allAssignedCps = this._transactions.map(tx => tx.counterparty.obj.name);
    const orphanAccounts = invalidEntryAccounts.filter(acName => allAssignedAccounts.findIndex(name => acName === name) === -1)
    const orphanCps = invalidEntryCps.filter(cpName => allAssignedCps.findIndex(name => cpName === name) === -1)
    
    this._accountContainer.remove(orphanAccounts);
    this._counterpartyContainer.remove(orphanCps);
  }

  private registerEntryKey(parsedRow: ParsedRow) {
    const registerEntry = parsedRow.registryEntry;
    if (!registerEntry) {
      return "";
    }
    const dateStr = registerEntry.date.toISOString().substr(0, 10);
    const counterpartyUuid = registerEntry.counterparty.uuid;
    return `${dateStr}_${counterpartyUuid}`;
  }

  private extractTransactions(parsedRows: ParsedRow[]): [Transaction[], ParsedRow[]] {
    const registerEntries = parsedRows.map(row => {
      if (!row.registryEntry) {
        throw new Error("Illegal state");
      }
      return row.registryEntry;
    });

    const count = registerEntries.length;
    switch (count) {
      case 0: {
        return [[], []];
      }
      case 1: {
        return [[], parsedRows.map(row => row.withError("Entries don't balance"))];
      }
      default: {
        const sum = registerEntries.map(ent => ent.amount.value).reduce(((a, b) => a + b), 0);
        if (areNumbersEqual(sum, 0)) {
          const transaction = new TransactionBuilder()
            .uuid(uuid())
            .date(registerEntries[0].date)
            .counterparty(registerEntries[0].counterparty)
            .addEntries(registerEntries.map(ent => ent.entry))
            .build();
          return [[transaction], []];
        }
        return [[], parsedRows.map(row => row.withError("Entries don't balance"))];
      }
    }
  }

  getInvalidRows() {
    return this._invalidEntries.concat(this._invalidTxEntries);
  }

  getTransactions() {
    return this._transactions;
  }

  getNewEntities(): EntityContainer {
    return new EntityContainer(this._accountContainer.getNew(), this._counterpartyContainer.getNew(), this._transactions);
  }
}

export { ImportProcessor, EntityContainer }